import React from 'react';
import { Link } from 'react-router-dom';

/**
 * HeroSection
 *
 * Props:
 *   eyebrow     {string}  — Small caps label above title
 *   title       {string}  — Main headline (can include \n for line breaks)
 *   subtitle    {string}  — Supporting copy
 *   ctaPrimary  {object}  — { label, to } — internal route
 *   ctaSecondary{object}  — { label, to }
 *   bgImage     {string}  — URL to hero photo (optional; gradient used if absent)
 *   size        {string}  — 'full' | 'medium' | 'small'
 *   align       {string}  — 'left' (default) | 'center'
 *
 * HERO IMAGE REPLACEMENT:
 *   When ready to add photos, pass the bgImage prop:
 *   <HeroSection bgImage="/images/hero-home.jpg" ... />
 *   or use an Unsplash URL:
 *   <HeroSection bgImage="https://images.unsplash.com/..." ... />
 */
export default function HeroSection({
  eyebrow,
  title,
  subtitle,
  ctaPrimary,
  ctaSecondary,
  bgImage,
  size = 'full',
  align = 'left',
}) {
  const bgStyle = bgImage
    ? { backgroundImage: `url(${bgImage})` }
    : {};

  const contentStyle =
    align === 'center'
      ? { textAlign: 'center', maxWidth: '860px', margin: '0 auto' }
      : { maxWidth: '760px' };

  return (
    <section
      className={`hero hero--${size}`}
      aria-label={`${title} — hero section`}
    >
      {/* Background layer */}
      <div className="hero__bg" style={bgStyle} />

      {/* Overlays */}
      <div className="hero__overlay" />
      <div className="hero__glow" />

      {/* Content */}
      <div className="hero__content">
        <div style={contentStyle}>

          {eyebrow && (
            <div
              className={`eyebrow eyebrow--light hero-anim-1${align === 'center' ? ' eyebrow--center' : ''}`}
            >
              {eyebrow}
            </div>
          )}

          {title && (
            <h1 className="d-xl t-white hero-anim-2">
              {title.split('\n').map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < title.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </h1>
          )}

          {subtitle && (
            <>
              <div className="hero__rule hero-anim-3" />
              <p
                className="lead lead--white hero-anim-3"
                style={{ maxWidth: align === 'center' ? '640px' : '540px', margin: align === 'center' ? '0 auto' : undefined }}
              >
                {subtitle}
              </p>
            </>
          )}

          {(ctaPrimary || ctaSecondary) && (
            <div
              className="hero__ctas hero-anim-4"
              style={{ justifyContent: align === 'center' ? 'center' : 'flex-start' }}
            >
              {ctaPrimary && (
                <Link to={ctaPrimary.to} className="btn btn-primary">
                  {ctaPrimary.label}
                </Link>
              )}
              {ctaSecondary && (
                <Link to={ctaSecondary.to} className="btn btn-outline-light">
                  {ctaSecondary.label}
                </Link>
              )}
            </div>
          )}

        </div>
      </div>
    </section>
  );
}
